/**
 * AIResultCards — Rich interactive cards for AI action results
 *
 * Renders structured action results as visual cards instead of relying
 * solely on the LLM's text response. Each card type maps to an action
 * category and displays the data in an easily scannable format.
 *
 * Card types:
 *  - TaskCard        → task.create, task.update, task.complete, task.assign
 *  - TaskListCard    → task.list (multiple tasks, clickable)
 *  - ProjectCard     → project.create, project.get, project.update
 *  - ProjectListCard → project.list, project.search (clickable)
 *  - StatsCard       → dashboard.stats, dashboard.workload
 *  - DeadlineCard    → dashboard.deadlines
 *  - MemberListCard  → project.listMembers, team.list, team.getMembers
 *  - NavigateCard    → navigate.switchWorkspace
 *
 * @module components/AIAssistant/AIResultCards
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../../utils/cn';

/* ─── Priority & Status Badge Helpers ───────────────────────────────────── */

const PRIORITY_STYLES = {
  urgent: 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  none: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
};

const STATUS_STYLES = {
  'to-do': 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  'in-progress': 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400',
  'in-review': 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-400',
  done: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
  cancelled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
  blocked: 'bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-400',
};

function PriorityBadge({ priority }) {
  if (!priority) return null;
  return (
    <span className={cn(
      'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide',
      PRIORITY_STYLES[priority] || PRIORITY_STYLES.none
    )}>
      {priority}
    </span>
  );
}

function StatusBadge({ status }) {
  if (!status) return null;
  const label = status.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <span className={cn(
      'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide',
      STATUS_STYLES[status] || STATUS_STYLES['to-do']
    )}>
      {label}
    </span>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

function getAssigneeName(assignedTo) {
  if (!assignedTo) return null;
  // assignedTo is an array of { user, assignedBy, assignedAt }
  if (Array.isArray(assignedTo) && assignedTo.length > 0) {
    const first = assignedTo[0];
    const user = first?.user;
    if (typeof user === 'string') return null;
    return user?.firstName
      ? `${user.firstName} ${user.lastName || ''}`.trim()
      : user?.email || null;
  }
  return null;
}

/* ─── TaskCard ─────────────────────────────────────────────────────────── */

function TaskCard({ data }) {
  if (!data) return null;
  const task = data;
  const assignee = getAssigneeName(task.assignedTo);
  const dueDate = formatDate(task.dueDate);
  const project = typeof task.project === 'object' ? task.project?.name : null;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/50 overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 dark:bg-emerald-950/20 border-b border-gray-100 dark:border-gray-800">
        <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">Task Created</span>
      </div>

      {/* Content */}
      <div className="px-3 py-2.5 space-y-2">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 leading-snug">
          {task.title}
        </p>

        {/* Badges */}
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={task.status} />
          <PriorityBadge priority={task.priority} />
        </div>

        {/* Details row */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-300">
          {assignee && (
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0" />
              </svg>
              {assignee}
            </span>
          )}
          {dueDate && (
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
              </svg>
              {dueDate}
            </span>
          )}
          {project && (
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
              </svg>
              {project}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── TaskListCard ─────────────────────────────────────────────────────── */

function TaskListCard({ data, message, navigate, isOrgWorkspace }) {
  // Handle both direct array and nested { tasks: [...], total } format
  const tasks = Array.isArray(data) ? data : (data?.tasks || []);
  if (tasks.length === 0) return null;

  const total = data?.total || tasks.length;
  const displayed = tasks.slice(0, 8);
  const remaining = total - displayed.length;

  const handleClick = (task) => {
    if (!navigate || !task.id) return;
    const path = isOrgWorkspace ? `/org/tasks/${task.id}` : `/tasks/${task.id}`;
    navigate(path);
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-blue-50 dark:bg-blue-950/20 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
          </svg>
          <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
            {total} Task{total !== 1 ? 's' : ''}
          </span>
        </div>
        {total > displayed.length && (
          <span className="text-[10px] text-blue-500 dark:text-blue-400">
            showing {displayed.length}
          </span>
        )}
      </div>

      {/* Task rows */}
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {displayed.map((task, idx) => (
          <div
            key={task.id || task._id || idx}
            className={cn(
              'flex items-center gap-2 px-3 py-2',
              task.id && navigate && 'cursor-pointer hover:bg-blue-50/50 dark:hover:bg-blue-950/10 transition-colors'
            )}
            onClick={() => handleClick(task)}
          >
            {/* Status dot */}
            <div className={cn(
              'w-1.5 h-1.5 rounded-full flex-shrink-0',
              task.status === 'done' ? 'bg-emerald-500' :
              task.status === 'in-progress' ? 'bg-blue-500' :
              task.status === 'blocked' ? 'bg-red-500' :
              task.status === 'in-review' ? 'bg-purple-500' :
              'bg-gray-400'
            )} />

            {/* Title */}
            <span className={cn(
              'flex-1 text-xs text-gray-700 dark:text-gray-300 truncate',
              task.status === 'done' && 'line-through text-gray-400 dark:text-gray-400',
              task.overdue && 'text-red-600 dark:text-red-400'
            )}>
              {task.title}
              {task.overdue && <span className="ml-1 text-[9px] text-red-500">overdue</span>}
            </span>

            {/* Priority + Due */}
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <PriorityBadge priority={task.priority} />
              {task.dueDate && (
                <span className="text-[10px] text-gray-400 dark:text-gray-400">
                  {formatDate(task.dueDate)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer — remaining count */}
      {remaining > 0 && (
        <div className="px-3 py-1.5 text-[10px] text-blue-500 dark:text-blue-400 text-center border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-blue-50/30 dark:hover:bg-blue-950/10 transition-colors"
          onClick={() => navigate && navigate(isOrgWorkspace ? '/org/tasks' : '/tasks')}
        >
          +{remaining} more — view all tasks →
        </div>
      )}
    </div>
  );
}

/* ─── ProjectCard ──────────────────────────────────────────────────────── */

function ProjectCard({ data }) {
  if (!data) return null;
  const project = data;
  const membersCount = Array.isArray(project.members) ? project.members.length : 0;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-950/20 border-b border-gray-100 dark:border-gray-800">
        <svg className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
        </svg>
        <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">Project</span>
      </div>

      {/* Content */}
      <div className="px-3 py-2.5 space-y-2">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{project.name}</p>
        {project.description && (
          <p className="text-xs text-gray-500 dark:text-gray-300 line-clamp-2">{project.description}</p>
        )}

        {/* Meta */}
        <div className="flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-300">
          {project.status && (
            <span className="flex items-center gap-1">
              <StatusBadge status={project.status} />
            </span>
          )}
          {membersCount > 0 && (
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
              </svg>
              {membersCount} member{membersCount !== 1 ? 's' : ''}
            </span>
          )}
          {project.startDate && (
            <span className="flex items-center gap-1">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75" />
              </svg>
              {formatDate(project.startDate)}
            </span>
          )}
        </div>

        {/* Progress bar */}
        {typeof project.progress === 'number' && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-[10px]">
              <span className="text-gray-500 dark:text-gray-300">Progress</span>
              <span className="font-medium text-gray-600 dark:text-gray-300">{project.progress}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
              <div
                className="h-full rounded-full bg-indigo-500 dark:bg-indigo-400 transition-all duration-500"
                style={{ width: `${Math.min(100, Math.max(0, project.progress))}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── StatsCard ────────────────────────────────────────────────────────── */

function StatsCard({ data }) {
  if (!data) return null;

  // Extract stat entries — handle both flat and nested shapes from dashboard.stats
  const stats = [];

  // Nested shape: { tasks: { total, todo, inProgress, done, overdue }, projects: { total, active }, completionRate }
  if (data.tasks && typeof data.tasks === 'object') {
    if (data.tasks.total != null) stats.push({ label: 'Total Tasks', value: data.tasks.total, color: 'blue' });
    if (data.tasks.done != null) stats.push({ label: 'Completed', value: data.tasks.done, color: 'emerald' });
    if (data.tasks.inProgress != null) stats.push({ label: 'In Progress', value: data.tasks.inProgress, color: 'blue' });
    if (data.tasks.overdue != null) stats.push({ label: 'Overdue', value: data.tasks.overdue, color: 'red' });
    if (data.tasks.todo != null) stats.push({ label: 'To Do', value: data.tasks.todo, color: 'amber' });
  }
  if (data.projects && typeof data.projects === 'object') {
    if (data.projects.total != null) stats.push({ label: 'Projects', value: data.projects.total, color: 'indigo' });
    if (data.projects.active != null) stats.push({ label: 'Active Projects', value: data.projects.active, color: 'indigo' });
  }
  if (data.completionRate != null) stats.push({ label: 'Completion Rate', value: `${data.completionRate}%`, color: 'emerald' });

  // Flat shape fallback (legacy)
  if (stats.length === 0) {
    if (data.totalTasks != null) stats.push({ label: 'Total Tasks', value: data.totalTasks, color: 'blue' });
    if (data.completedTasks != null) stats.push({ label: 'Completed', value: data.completedTasks, color: 'emerald' });
    if (data.overdueTasks != null) stats.push({ label: 'Overdue', value: data.overdueTasks, color: 'red' });
    if (data.inProgressTasks != null) stats.push({ label: 'In Progress', value: data.inProgressTasks, color: 'blue' });
    if (data.totalProjects != null) stats.push({ label: 'Projects', value: data.totalProjects, color: 'indigo' });
    if (data.completionRate != null) stats.push({ label: 'Completion Rate', value: `${data.completionRate}%`, color: 'emerald' });
  }

  // Workload data
  if (data.totalActive != null) stats.push({ label: 'Active', value: data.totalActive, color: 'blue' });
  if (data.urgentHighCount != null) stats.push({ label: 'Urgent/High', value: data.urgentHighCount, color: 'red' });
  if (data.byStatus && typeof data.byStatus === 'object') {
    Object.entries(data.byStatus).forEach(([status, count]) => {
      if (stats.length < 8) {
        const colorMap = { 'to-do': 'amber', 'in-progress': 'blue', 'in-review': 'indigo', done: 'emerald', blocked: 'red' };
        stats.push({ label: status.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), value: count, color: colorMap[status] || 'blue' });
      }
    });
  }

  if (stats.length === 0) return null;

  const colorMap = {
    blue: 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300',
    emerald: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300',
    red: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300',
    indigo: 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-300',
    amber: 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300',
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 dark:bg-purple-950/20 border-b border-gray-100 dark:border-gray-800">
        <svg className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
        </svg>
        <span className="text-xs font-medium text-purple-700 dark:text-purple-300">Statistics</span>
      </div>

      <div className="grid grid-cols-2 gap-2 p-3">
        {stats.map((stat, idx) => (
          <div key={idx} className={cn('rounded-md px-2.5 py-2', colorMap[stat.color] || colorMap.blue)}>
            <p className="text-lg font-bold leading-tight">{stat.value}</p>
            <p className="text-[10px] opacity-70 font-medium">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── MemberListCard ───────────────────────────────────────────────────── */

function MemberListCard({ data }) {
  if (!Array.isArray(data) || data.length === 0) return null;

  const displayed = data.slice(0, 8);
  const remaining = data.length - displayed.length;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-teal-50 dark:bg-teal-950/20 border-b border-gray-100 dark:border-gray-800">
        <svg className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
        </svg>
        <span className="text-xs font-medium text-teal-700 dark:text-teal-300">
          {data.length} Member{data.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {displayed.map((member, idx) => {
          // Members can be: { user: { firstName, lastName, email }, role } or directly a user object
          const user = member?.user || member;
          const name = user?.firstName
            ? `${user.firstName} ${user.lastName || ''}`.trim()
            : user?.email || 'Unknown';
          const role = member?.role || '';

          return (
            <div key={user?._id || idx} className="flex items-center gap-2.5 px-3 py-2">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-white uppercase">
                  {name.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">{name}</p>
                {user?.email && (
                  <p className="text-[10px] text-gray-400 dark:text-gray-400 truncate">{user.email}</p>
                )}
              </div>
              {role && (
                <span className="text-[10px] font-medium text-gray-400 dark:text-gray-400 capitalize flex-shrink-0">
                  {role}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {remaining > 0 && (
        <div className="px-3 py-1.5 text-[10px] text-gray-400 dark:text-gray-400 text-center border-t border-gray-100 dark:border-gray-800">
          +{remaining} more
        </div>
      )}
    </div>
  );
}

/* ─── ProjectListCard ──────────────────────────────────────────────────── */

function ProjectListCard({ data, message, navigate, isOrgWorkspace }) {
  const projects = Array.isArray(data) ? data : (data?.projects || []);
  if (projects.length === 0) return null;

  const total = data?.total || projects.length;
  const displayed = projects.slice(0, 8);
  const remaining = total - displayed.length;

  const handleClick = (project) => {
    if (!navigate || !project.id) return;
    const path = isOrgWorkspace ? `/org/projects/${project.id}` : `/projects/${project.id}`;
    navigate(path);
  };

  const STATUS_COLORS = {
    planning: 'bg-amber-400',
    active: 'bg-emerald-400',
    'on-hold': 'bg-orange-400',
    completed: 'bg-blue-400',
    cancelled: 'bg-gray-400',
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/50 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-indigo-50 dark:bg-indigo-950/20 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 0 1 4.5 9.75h15A2.25 2.25 0 0 1 21.75 12v.75m-8.69-6.44-2.12-2.12a1.5 1.5 0 0 0-1.061-.44H4.5A2.25 2.25 0 0 0 2.25 6v12a2.25 2.25 0 0 0 2.25 2.25h15A2.25 2.25 0 0 0 21.75 18V9a2.25 2.25 0 0 0-2.25-2.25h-5.379a1.5 1.5 0 0 1-1.06-.44Z" />
          </svg>
          <span className="text-xs font-medium text-indigo-700 dark:text-indigo-300">
            {total} Project{total !== 1 ? 's' : ''}
          </span>
        </div>
        {total > displayed.length && (
          <span className="text-[10px] text-indigo-500 dark:text-indigo-400">
            showing {displayed.length}
          </span>
        )}
      </div>

      {/* Project rows */}
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {displayed.map((project, idx) => (
          <div
            key={project.id || project._id || idx}
            className={cn(
              'flex items-center gap-2.5 px-3 py-2',
              project.id && navigate && 'cursor-pointer hover:bg-indigo-50/50 dark:hover:bg-indigo-950/10 transition-colors'
            )}
            onClick={() => handleClick(project)}
          >
            {/* Status indicator */}
            <div className={cn(
              'w-1.5 h-1.5 rounded-full flex-shrink-0',
              STATUS_COLORS[project.status] || 'bg-gray-400'
            )} />

            {/* Title + meta */}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                {project.title || project.name}
              </p>
              <div className="flex items-center gap-2 mt-0.5">
                {project.status && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-400 capitalize">{project.status}</span>
                )}
                {project.memberCount != null && (
                  <span className="text-[10px] text-gray-400 dark:text-gray-400">
                    {project.memberCount} member{project.memberCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>

            {/* Progress + Priority */}
            <div className="flex items-center gap-2 flex-shrink-0">
              {typeof project.progress === 'number' && (
                <div className="flex items-center gap-1">
                  <div className="w-12 h-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-indigo-500 dark:bg-indigo-400"
                      style={{ width: `${Math.min(100, project.progress)}%` }}
                    />
                  </div>
                  <span className="text-[10px] text-gray-400 w-6 text-right">{project.progress}%</span>
                </div>
              )}
              <PriorityBadge priority={project.priority} />
            </div>

            {/* Clickable arrow */}
            {project.id && navigate && (
              <svg className="w-3 h-3 text-gray-300 dark:text-gray-600 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
              </svg>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      {remaining > 0 && (
        <div className="px-3 py-1.5 text-[10px] text-indigo-500 dark:text-indigo-400 text-center border-t border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-indigo-50/30 dark:hover:bg-indigo-950/10 transition-colors"
          onClick={() => navigate && navigate(isOrgWorkspace ? '/org/projects' : '/projects')}
        >
          +{remaining} more — view all projects →
        </div>
      )}
    </div>
  );
}

/* ─── DeadlineCard ─────────────────────────────────────────────────────── */

function DeadlineCard({ data, navigate, isOrgWorkspace }) {
  const deadlines = Array.isArray(data) ? data : (data?.deadlines || []);
  if (deadlines.length === 0) return null;

  const displayed = deadlines.slice(0, 6);
  const remaining = deadlines.length - displayed.length;

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700/60 bg-white dark:bg-gray-900/50 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/20 border-b border-gray-100 dark:border-gray-800">
        <svg className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
        <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
          Upcoming Deadlines
        </span>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {displayed.map((item, idx) => {
          const isUrgent = item.daysLeft != null && item.daysLeft <= 1;
          const isSoon = item.daysLeft != null && item.daysLeft <= 3;
          return (
            <div key={idx} className="flex items-center gap-2 px-3 py-2">
              <div className={cn(
                'w-1.5 h-1.5 rounded-full flex-shrink-0',
                isUrgent ? 'bg-red-500' : isSoon ? 'bg-amber-500' : 'bg-blue-400'
              )} />
              <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 truncate">{item.title}</span>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <PriorityBadge priority={item.priority} />
                {item.daysLeft != null && (
                  <span className={cn(
                    'text-[10px] font-medium',
                    isUrgent ? 'text-red-500' : isSoon ? 'text-amber-500' : 'text-gray-400'
                  )}>
                    {item.daysLeft === 0 ? 'today' : item.daysLeft === 1 ? 'tomorrow' : `${item.daysLeft}d`}
                  </span>
                )}
                {item.dueDate && !item.daysLeft && (
                  <span className="text-[10px] text-gray-400">{formatDate(item.dueDate)}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {remaining > 0 && (
        <div className="px-3 py-1.5 text-[10px] text-amber-500 dark:text-amber-400 text-center border-t border-gray-100 dark:border-gray-800">
          +{remaining} more deadline{remaining !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

/* ─── Action → Card Mapping ────────────────────────────────────────────── */

/**
 * Map of action name patterns to card renderer components.
 * Ordered by specificity — first match wins.
 * `extract` converts the raw `result.data` into the shape the card expects.
 */
const CARD_MAP = [
  // Task actions
  { pattern: /^task\.(create|update|complete)$/i, component: TaskCard, extract: (d) => d },
  { pattern: /^task\.list$/i, component: TaskListCard, extract: (d) => d },
  { pattern: /^task\.assign$/i, component: TaskCard, extract: (d) => d },
  // Project actions
  { pattern: /^project\.(create|get|update)$/i, component: ProjectCard, extract: (d) => d },
  { pattern: /^project\.(list|search)$/i, component: ProjectListCard, extract: (d) => d },
  { pattern: /^project\.listMembers$/i, component: MemberListCard, extract: (d) => d?.members || d },
  // Dashboard / stats
  { pattern: /^dashboard\.(getStats|getWorkload|stats|workload)/i, component: StatsCard, extract: (d) => d },
  { pattern: /^dashboard\.(deadlines|getDeadlines)/i, component: DeadlineCard, extract: (d) => d },
  // Team
  { pattern: /^team\.(list|getMembers|members)/i, component: MemberListCard, extract: (d) => d?.members || d },
];

/**
 * Resolve which card renderer to use for a given action.
 * @param {string} actionName - e.g. "task.create", "project.get"
 * @returns {{ component: React.ComponentType, extract: Function } | null}
 */
function resolveCard(actionName) {
  if (!actionName) return null;
  for (const entry of CARD_MAP) {
    if (entry.pattern.test(actionName)) {
      return { component: entry.component, extract: entry.extract };
    }
  }
  return null;
}

/* ─── Public API ───────────────────────────────────────────────────────── */

/**
 * Render a rich result card for an AI action result.
 *
 * @param {Object} props
 * @param {string} props.actionName - The action ID (e.g. "task.create")
 * @param {Object} props.result - The action result object { success, data, message }
 * @returns {React.ReactElement|null}
 */
export default function AIResultCard({ actionName, result }) {
  const navigate = useNavigate();
  const location = useLocation();
  const isOrgWorkspace = location.pathname.startsWith('/org/');

  // Don't render cards for failed actions or confirmation-pending actions
  if (!result?.success || result?.requiresConfirmation) return null;

  const resolved = resolveCard(actionName);
  if (!resolved) return null;

  const { component: CardComponent, extract } = resolved;
  const data = extract(result.data);

  // Validate we have some data to render
  if (!data || (typeof data !== 'object')) return null;

  return (
    <div className="mt-2 w-full animate-fade-in">
      <CardComponent data={data} message={result.message} navigate={navigate} isOrgWorkspace={isOrgWorkspace} />
    </div>
  );
}

export { TaskCard, TaskListCard, ProjectCard, ProjectListCard, StatsCard, DeadlineCard, MemberListCard };
